import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getScan } from '../../../api/scans';
import { getBodySystemsList } from '../../../api/body-systems';
import {
  generateOneBodySystemSummary,
  getMemberHealthSummaryList
} from '../../../api/healthSummary';
import Button from '../../BaseComponents/Button';
import Loader from '../../BaseComponents/Loader';
import {
  FbsReportData,
  resolveUsesFbsFindingStatusV2
} from '../../FullBodyScan/fullBodyScanReportUtils';
import {
  HealthSummaryResponseElement,
  ListBodySystemResponseElement
} from '../../../types/apiGeneratedTypes';
import BodySystemAnalysisContent from './BodySystemAnalysisContent';
import GenerateBodySystemModal, { GenerateProgress } from './GenerateBodySystemModal';
import { getBodySystemsWithContent } from './bodySystemAnalysisUtils';
import './BodySystemAnalysisTab.scss';

const BodySystemAnalysisTab: React.FC = () => {
  const location = useLocation();
  const memberId = location.pathname.split('/')[2];
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingSystems, setIsLoadingSystems] = useState(false);
  const [bodySystems, setBodySystems] = useState<ListBodySystemResponseElement[]>([]);
  const [reportData, setReportData] = useState<FbsReportData>({});
  const [scanPayload, setScanPayload] = useState<Record<string, unknown>>({});
  const [healthSummaries, setHealthSummaries] = useState<HealthSummaryResponseElement[]>([]);
  const [progress, setProgress] = useState<GenerateProgress | null>(null);
  const cancelGenerationRef = useRef(false);

  const loadHealthSummaries = useCallback(async () => {
    if (!memberId) return;
    try {
      const response = await getMemberHealthSummaryList(memberId);
      setHealthSummaries(response.health_summaries ?? []);
    } catch (error) {
      console.error(error);
      setHealthSummaries([]);
    }
  }, [memberId]);

  const loadBodySystems = useCallback(async () => {
    setIsLoadingSystems(true);
    try {
      const response = await getBodySystemsList();
      const systems = response.body_systems ?? [];
      const withContent = getBodySystemsWithContent(systems);
      setBodySystems(withContent.length > 0 ? withContent : systems);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load body systems');
      setBodySystems([]);
    } finally {
      setIsLoadingSystems(false);
    }
  }, []);

  useEffect(() => {
    const fetchScanData = async () => {
      if (!memberId) return;

      try {
        setIsLoading(true);
        const [scanData] = await Promise.all([
          getScan(memberId),
          loadHealthSummaries(),
          loadBodySystems()
        ]);
        setReportData((scanData?.report_data || {}) as FbsReportData);
        setScanPayload((scanData || {}) as Record<string, unknown>);
      } catch (error: any) {
        const errorMessage = error?.detail || error?.message || 'Failed to fetch scan data';
        toast.error(errorMessage);
        setReportData({});
        setScanPayload({});
      } finally {
        setIsLoading(false);
      }
    };

    fetchScanData();
  }, [memberId, loadHealthSummaries, loadBodySystems]);

  const usesFbsFindingStatusV2 = useMemo(
    () => resolveUsesFbsFindingStatusV2(scanPayload, reportData),
    [scanPayload, reportData]
  );

  const hasAnyGenerated = healthSummaries.some(
    (summary) => Boolean(summary.details?.trim()) || Boolean(summary.score?.trim())
  );

  const handleOpenModal = () => {
    if (isGenerating) return;
    setProgress(null);
    setIsModalOpen(true);
    if (bodySystems.length === 0) {
      void loadBodySystems();
    }
  };

  const handleCloseModal = () => {
    if (isGenerating) {
      cancelGenerationRef.current = true;
      toast.info('Stopping after the current body system finishes');
      return;
    }
    setIsModalOpen(false);
  };

  const handleGenerate = async (bodySystemIds: number[]) => {
    if (!memberId || isGenerating || bodySystemIds.length === 0) return;

    const selectedSystems = bodySystemIds
      .map((id) => bodySystems.find((bodySystem) => bodySystem.id === id))
      .filter((bodySystem): bodySystem is ListBodySystemResponseElement => Boolean(bodySystem));

    if (selectedSystems.length === 0) {
      toast.error('Select at least one body system');
      return;
    }

    cancelGenerationRef.current = false;
    setIsGenerating(true);
    let successCount = 0;
    let failureCount = 0;
    const failedTitles: string[] = [];

    try {
      for (let index = 0; index < selectedSystems.length; index += 1) {
        if (cancelGenerationRef.current) {
          break;
        }

        const bodySystem = selectedSystems[index];
        setProgress({
          current: index + 1,
          total: selectedSystems.length,
          title: bodySystem.title,
          completed: successCount
        });

        try {
          const response = await generateOneBodySystemSummary(memberId, bodySystem.id);
          if (response.success_count > 0) {
            successCount += 1;
          } else {
            failureCount += 1;
            failedTitles.push(bodySystem.title);
          }
        } catch (error: unknown) {
          failureCount += 1;
          failedTitles.push(bodySystem.title);
          const errorMessage =
            error instanceof Error ? error.message : `Failed to generate ${bodySystem.title}`;
          toast.error(errorMessage);
        }

        setProgress({
          current: index + 1,
          total: selectedSystems.length,
          title: bodySystem.title,
          completed: successCount
        });
        await loadHealthSummaries();
      }

      if (cancelGenerationRef.current && successCount + failureCount < selectedSystems.length) {
        toast.warning(
          `Stopped after ${successCount + failureCount}/${selectedSystems.length}. ${successCount} generated.`
        );
      } else if (successCount > 0 && failureCount === 0) {
        toast.success(
          `${successCount}/${selectedSystems.length} generated`
        );
      } else if (successCount > 0) {
        toast.warning(
          `${successCount}/${selectedSystems.length} generated. Failed: ${failedTitles.join(', ')}`
        );
      } else {
        toast.error('No body system summaries could be generated');
      }
    } finally {
      setIsGenerating(false);
      setProgress(null);
      setIsModalOpen(false);
      cancelGenerationRef.current = false;
    }
  };

  if (isLoading) {
    return (
      <div className="body-system-analysis-tab body-system-analysis-tab--loading">
        <Loader />
      </div>
    );
  }

  return (
    <div className="body-system-analysis-tab ">
      <div className="body-system-analysis-tab__header sideBarPad2">
        <div className="body-system-analysis-tab__header-text">
          <h2>Bodysystem Analysis</h2>
          <p>
            Review biomarkers and scan findings by body system. Generate AI score and summary for
            all body systems or a selected set.
          </p>
        </div>
        <div className="body-system-analysis-tab__actions">
          <Button
            text={hasAnyGenerated ? 'Regenerate' : 'Generate'}
            onClick={handleOpenModal}
            isSecondary={true}
            variant="refresh"
            isLoading={isGenerating}
            isDisabled={isGenerating}
            height="36px"
            paddingTop="8px"
            paddingBottom="8px"
            paddingLeft="16px"
            paddingRight="16px"
            borderRadius="10px"
          />
        </div>
      </div>
      {isGenerating && progress ? (
        <div className="body-system-analysis-tab__generating">
          <Loader />
          <strong>
            {progress.completed}/{progress.total} generated
          </strong>
          <p>
            Generating {progress.current}/{progress.total}: {progress.title}
          </p>
        </div>
      ) : null}
      <BodySystemAnalysisContent
        memberId={memberId}
        reportData={reportData}
        usesFbsFindingStatusV2={usesFbsFindingStatusV2}
        healthSummaries={healthSummaries}
      />
      <GenerateBodySystemModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        bodySystems={bodySystems}
        isLoadingList={isLoadingSystems}
        isGenerating={isGenerating}
        progress={progress}
        onGenerate={(ids) => void handleGenerate(ids)}
      />
    </div>
  );
};

export default BodySystemAnalysisTab;
